import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { BRANDING } from '@/lib/branding'

export type DensityPreset = 'compact' | 'default'

const DENSITY_FONT_SIZE: Record<DensityPreset, string> = {
  compact: '14px',
  default: '15px',
}

const DENSITY_PRESETS: DensityPreset[] = ['compact', 'default']
const DENSITY_STORAGE_KEY = 'ui-density'

interface ColorThemeContextValue {
  density: DensityPreset
  setDensity: (density: DensityPreset) => void
}

const ColorThemeContext = createContext<ColorThemeContextValue | null>(null)

function readDensity(): DensityPreset {
  try {
    const v = localStorage.getItem(DENSITY_STORAGE_KEY)
    if (v && DENSITY_PRESETS.includes(v as DensityPreset)) return v as DensityPreset
  } catch { /* noop */ }
  return 'default'
}

export function ColorThemeProvider({ children }: { children: ReactNode }) {
  const [density, setDensityState] = useState<DensityPreset>(readDensity)

  const setDensity = useCallback((d: DensityPreset) => {
    setDensityState(d)
    try { localStorage.setItem(DENSITY_STORAGE_KEY, d) } catch { /* noop */ }
  }, [])

  // The palette follows the tenant brand; it is not a user preference.
  useEffect(() => {
    document.documentElement.classList.add(`theme-${BRANDING.colorTheme}`)
  }, [])

  useEffect(() => {
    document.documentElement.style.fontSize = DENSITY_FONT_SIZE[density]
  }, [density])

  return (
    <ColorThemeContext.Provider value={{ density, setDensity }}>
      {children}
    </ColorThemeContext.Provider>
  )
}

export function useColorTheme(): ColorThemeContextValue {
  const ctx = useContext(ColorThemeContext)
  if (!ctx) throw new Error('useColorTheme must be used within ColorThemeProvider')
  return ctx
}
